import { memo, useCallback } from 'react';

import { type LabelDisplayedRowsArgs, TablePagination } from '@mui/material';
import { type PaginationState, type Updater } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { useStyles as usePaginationStandardStyles } from 'components/tables/dndStandardTable/tableComponents/styles';

import PaginationActions from './PaginationActions';

type PaginationClasses = 'paginationCaption' | 'menuItem' | 'toolbar' | 'select' | 'action';

interface EnhancedPaginationProps {
  classNames?: Partial<Record<PaginationClasses, string>>;
  manualPagination?: boolean;
  paginationOptions?: (number | { label: string; value: number })[];
  paginationState: PaginationState;
  showActions?: boolean;
  onGetPageCount: () => number;
  onGetRowCount: () => number;
  onPageChange?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, page: number) => void;
  onPaginationSizeChange?: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  onSetPageIndex: (updater: Updater<number>) => void;
  onSetPageSize: (updater: Updater<number>) => void;
}

const EnhancedPagination: React.FC<React.PropsWithChildren<EnhancedPaginationProps>> = ({
  classNames,
  manualPagination = false,
  paginationOptions,
  paginationState,
  showActions = false,
  onGetPageCount,
  onGetRowCount,
  onPageChange,
  onPaginationSizeChange,
  onSetPageIndex,
  onSetPageSize,
}) => {
  const { t } = useTranslation();
  const { classes: paginationStandardClasses, cx } = usePaginationStandardStyles();

  const { pageIndex, pageSize } = paginationState;
  const rowsCount = manualPagination ? onGetPageCount() : onGetRowCount();
  const numberOfPages = Math.ceil(rowsCount / pageSize);
  const showingTo = (pageIndex + 1) * pageSize <= rowsCount ? (pageIndex + 1) * pageSize : rowsCount;

  const handlePageChange = useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
      onSetPageIndex(index);
    },
    [onSetPageIndex],
  );

  const handlePaginationSizeChange = useCallback(
    (event) => {
      const { value } = event.target;
      if (value) onSetPageSize(value === -1 ? onGetRowCount() : Number(value));
    },
    [onGetRowCount, onSetPageSize],
  );

  const labelDisplayedRows = useCallback(
    ({ from, to, count }: LabelDisplayedRowsArgs) => `${from}-${to} of ${count}`,
    [],
  );

  return (
    <TablePagination
      ActionsComponent={() =>
        pageSize === -1 || !showActions ? null : (
          <PaginationActions
            classNames={classNames}
            numberOfPages={numberOfPages}
            pageIndex={pageIndex}
            onSetPageIndex={onSetPageIndex}
          />
        )
      }
      classes={{
        spacer: paginationStandardClasses.paginationSpacer,
        selectLabel: classNames?.paginationCaption || paginationStandardClasses.paginationCaption,
        displayedRows: classNames?.paginationCaption || paginationStandardClasses.paginationCaption,
        toolbar: cx(classNames?.toolbar, paginationStandardClasses.toolbar),
        menuItem: classNames?.menuItem || paginationStandardClasses.menuItem,
        select: classNames?.select,
      }}
      component="div"
      count={rowsCount}
      defaultValue={25}
      labelDisplayedRows={
        labelDisplayedRows || (() => `${pageIndex * pageSize + 1}-${showingTo} of ${onGetPageCount()}`)
      }
      labelRowsPerPage={t('showing')}
      page={pageIndex}
      rowsPerPage={pageSize}
      rowsPerPageOptions={paginationOptions}
      slotProps={{
        select: {
          className: paginationOptions
            ? classNames?.select || paginationStandardClasses.select
            : paginationStandardClasses.selectHidden,
        },
      }}
      onPageChange={manualPagination ? onPageChange : handlePageChange}
      onRowsPerPageChange={manualPagination ? onPaginationSizeChange : handlePaginationSizeChange}
    />
  );
};

export default memo(EnhancedPagination);
